import { useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getContext } from '../lib/db';
import { StatCard, StatusBar, StatChips, DamageGauge, damageTone } from '../components/metrics';
import { PeriodFilter, ActivityRow, periodLabel } from '../components/PeriodFilter';
import { POSITIONS, ACTION_LABELS, ROLE_LABELS, SITE_STATUS } from '../lib/status';
import { Button } from '../components/ui/button';
import { Building2, Boxes, Layers, TriangleAlert, Activity, ArrowRight, ClipboardList, MapPin, Megaphone, Loader2, RotateCcw, CalendarClock, Receipt } from 'lucide-react';

export default function Home() {
  const { user } = useAuth();
  if (user.role === 'society_manager' && user.society_id) return <Navigate to={`/societies/${user.society_id}`} replace />;
  if (user.role === 'billing') return <Navigate to="/societies" replace />;
  if (user.role === 'operator') return <OperatorHome />;
  if (user.role === 'client') return <ClientHome />;
  return <AdminDashboard />;
}

function useDashboard(days = 30) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/dashboard?days=${days}`).then((r) => setData(r.data)); }, [days]);
  return data;
}

function AdminDashboard() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const d = useDashboard(days);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const o = d.overall;
  return (
    <div className="space-y-6">
      <div>
        <p className="overline text-primary">{ROLE_LABELS[user.role]} · Command Center</p>
        <h1 className="font-head text-3xl font-bold">Operations Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard testid="stat-societies" label="Societies" value={d.counts.societies} sub={`${d.counts.towers} towers · ${d.counts.lifts} lifts`} icon={Building2} tone="text-primary" />
        <StatCard testid="stat-sites" label="Total Sites" value={o.total} sub={`${o.installed} deployed`} icon={Layers} />
        <StatCard testid="stat-occupancy" label="Occupancy Rate" value={`${o.occupancy_pct}%`} sub={`${o.occupied}/${o.total} sites with a live campaign`} icon={Boxes} tone="text-blue-400" />
        <StatCard testid="stat-damage" label="Damage Rate" value={`${o.damage_pct}%`} sub={`${o.damaged} damaged / awaiting repair`} icon={TriangleAlert} tone={damageTone(o.damage_pct)} />
      </div>

      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">Activity · {periodLabel(days)}</p>
        <PeriodFilter value={days} onChange={setDays} />
      </div>
      <ActivityRow activity={d.activity} />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <p className="font-head text-lg font-bold">Portfolio Health</p>
            <Link to="/societies" className="text-xs text-primary hover:underline">View all societies →</Link>
          </div>
          <StatusBar counts={o.status_counts} total={o.total} className="mb-3" />
          <StatChips counts={o.status_counts} />
          <div className="mt-5 space-y-2">
            <p className="overline text-muted-foreground">Societies by damage rate</p>
            {d.per_society.map((s) => (
              <Link key={s.id} to={`/societies/${s.id}`} data-testid={`dash-society-${s.id}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-background/50 p-3 hover:border-primary">
                <Building2 className="h-4 w-4 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{s.name}</p>
                  <StatusBar counts={s.stats.status_counts} total={s.stats.total} className="mt-1" />
                </div>
                <span className={`font-head text-lg font-bold ${damageTone(s.stats.damage_pct)}`}>{s.stats.damage_pct}%</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="mb-2 font-head text-lg font-bold">Overall Damage</p>
            <DamageGauge stats={o} />
          </div>
          {d.billing && (
            <div className="rounded-xl border border-border bg-card p-5" data-testid="billing-insights">
              <div className="mb-2 flex items-center justify-between">
                <p className="flex items-center gap-2 font-head text-lg font-bold"><Receipt className="h-4 w-4 text-primary" /> Billing</p>
                {d.billing.overdue_count > 0 && <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase text-rose-300">{d.billing.overdue_count} overdue</span>}
              </div>
              <p className="font-head text-2xl font-extrabold text-amber-400">₹{Number(d.billing.total_outstanding).toLocaleString('en-IN')}</p>
              <p className="text-xs text-muted-foreground">total outstanding</p>
              <div className="mt-2 space-y-1">
                {d.billing.items.slice(0, 4).map((it) => (
                  <Link key={it.society_id} to={`/societies/${it.society_id}`} className="flex items-center justify-between text-xs hover:text-primary">
                    <span className="truncate">{it.society_name}</span>
                    <span className={it.overdue ? 'text-rose-400' : 'text-muted-foreground'}>₹{Number(it.outstanding).toLocaleString('en-IN')}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="mb-3 flex items-center gap-2 font-head text-lg font-bold"><Activity className="h-4 w-4 text-primary" /> Recent Activity</p>
            <div className="space-y-2">
              {d.recent.slice(0, 8).map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-xs">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: (SITE_STATUS[r.new_status] || {}).dot }} />
                  <span className="flex-1 truncate">{ACTION_LABELS[r.action_type]} · {POSITIONS[r.position_type] || 'site'}</span>
                  <span className="truncate text-muted-foreground">{r.society_name}</span>
                </div>
              ))}
              {!d.recent.length && <p className="text-xs text-muted-foreground">No activity yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OperatorHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [insps, setInsps] = useState(null);
  const [resume, setResume] = useState(null);
  useEffect(() => {
    api.get('/dashboard').then((r) => setInsps(r.data.inspections || []));
    getContext(user.id).then((c) => c?.liftId && setResume(c));
  }, [user.id]);
  if (insps === null) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div className="space-y-5">
      <div><p className="overline text-primary">Field Operator</p><h1 className="font-head text-3xl font-bold">My Inspections</h1></div>

      {resume && (
        <div className="rounded-xl border-2 border-primary/50 bg-primary/10 p-4">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-medium"><MapPin className="h-4 w-4 text-primary" /> Resume where you left off</p>
            <Button size="sm" data-testid="resume-context-btn" onClick={() => navigate(`/lifts/${resume.liftId}`)}><RotateCcw className="mr-1 h-4 w-4" /> Resume</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {insps.map((i) => (
          <Link key={i.id} to={`/inspections/${i.id}`} data-testid={`op-inspection-${i.id}`}
            className="tile-press flex items-center justify-between gap-3 rounded-xl border-2 border-border bg-card p-4 hover:border-primary">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-head text-lg font-bold">{i.society_name}</p>
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase text-muted-foreground">{i.mode === 'install' ? 'Install' : 'Inspect'}{i.scope === 'multi' ? ' · Multi' : ''}</span>
              </div>
              <p className="truncate text-xs text-muted-foreground">{i.campaign_name || 'All campaigns'} · {i.site_ids.length} sites</p>
              {i.due_date && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> Due {i.due_date.slice(0, 10)}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="rounded-full border border-sky-600 bg-sky-950/50 px-2 py-0.5 text-[10px] font-mono uppercase text-sky-300">{i.status}</span>
              <ArrowRight className="h-5 w-5 text-primary" />
            </div>
          </Link>
        ))}
        {!insps.length && <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground"><ClipboardList className="mx-auto mb-2 h-8 w-8" />No inspections assigned yet.</div>}
      </div>
    </div>
  );
}

function ClientHome() {
  const { user } = useAuth();
  const d = useDashboard();
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div className="space-y-5">
      <div><p className="overline text-primary">Client Portal</p><h1 className="font-head text-3xl font-bold">My Campaigns</h1></div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Campaigns" value={(d.campaigns || []).length} icon={Megaphone} tone="text-primary" />
        <StatCard label="My Sites" value={d.overall.total} icon={Layers} />
        <StatCard label="Damage" value={`${d.overall.damage_pct}%`} icon={TriangleAlert} tone={damageTone(d.overall.damage_pct)} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {(d.campaigns || []).map((c) => (
          <Link key={c.id} to={`/campaigns/${c.id}`} data-testid={`client-campaign-${c.id}`}
            className="rounded-xl border border-border bg-card p-4 hover:border-primary">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-head text-lg font-bold">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.client_name} · {c.site_ids?.length || 0} sites</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </Link>
        ))}
        {!(d.campaigns || []).length && <p className="text-sm text-muted-foreground">No campaigns assigned to your account yet.</p>}
      </div>
    </div>
  );
}
